/**
 * EOD V2 modal — "End of Day Report" (2026-07-14 short-summary model).
 * Opens on Clock Out; submit is ATOMIC with clock-out (AttendanceWidget).
 *  - Read-only "Tasks touched today" list, grouped by client (no typing).
 *  - ONE required "Today's summary" box — NO minimum length.
 *  - Optional "Blockers" and "Plan for tomorrow" fields.
 * Typed text is never lost on save errors.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { TaskStatusIndicator } from '../TaskStatusIndicator';

export default function EodModal({ open, onOpenChange, tasks, submitting, onSubmit }) {
  const [summary, setSummary] = useState('');
  const [blockers, setBlockers] = useState('');
  const [planTomorrow, setPlanTomorrow] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setError('');
  }, [open]);

  // Group the read-only touched-task list by client for a compact snapshot.
  const grouped = useMemo(() => {
    const g = {};
    for (const t of tasks || []) {
      const key = t.client_name || 'No client';
      (g[key] = g[key] || []).push(t);
    }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [tasks]);

  const handleSubmit = () => {
    if (!summary.trim()) {
      setError("Please write a short summary of your day — a couple of lines is fine.");
      return;
    }
    setError('');
    onSubmit({ summary: summary.trim(), blockers: blockers.trim(), planTomorrow: planTomorrow.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="eod-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-gold" /> End of Day Report
          </DialogTitle>
          <DialogDescription>
            Your tasks for today are listed automatically — just write a short,
            client-based summary of the day. No per-task notes needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
          {/* Read-only auto task list */}
          <div className="space-y-2">
            <span className="text-[13px] font-medium">Tasks touched today <span className="text-muted-foreground font-normal">(auto — nothing to fill in)</span></span>
            {(tasks || []).length === 0 ? (
              <p className="text-[13px] text-muted-foreground py-1" data-testid="eod-no-tasks">
                No task activity detected today.
              </p>
            ) : (
              <div className="border border-border/40 rounded-lg divide-y divide-border/30" data-testid="eod-tasks-touched">
                {grouped.map(([client, list]) => (
                  <div key={client} className="px-3 py-2" data-testid={`eod-client-group-${client}`}>
                    <div className="text-[11px] font-semibold text-gold mb-1">{client} <span className="text-muted-foreground font-normal">({list.length})</span></div>
                    <div className="space-y-1">
                      {list.map((t) => (
                        <div key={t.task_id} className="flex items-center gap-2" data-testid={`eod-task-${t.task_id}`}>
                          <span className="text-[12px] flex-1 truncate">{t.title}</span>
                          {t.subtasks_touched?.length > 0 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold/10 text-gold shrink-0" title={t.subtasks_touched.join(', ')}>
                              {t.subtasks_touched.length} subtask{t.subtasks_touched.length === 1 ? '' : 's'}
                            </span>
                          )}
                          <TaskStatusIndicator status={t.status} size="xs" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Single required summary — NO minimum length */}
          <div className="space-y-1">
            <span className="text-[13px] font-medium">Today’s summary <span className="text-red-400">(required)</span></span>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Example: ‘DICH – finished ~15 stories; Hwy1 – designed 10 posts; Balgonie – edited and captioned 5 reels; ESM – created 2 logo concepts.’"
              rows={4}
              className="text-[13px]"
              data-testid="eod-summary"
            />
            {error && <p className="text-[11px] text-red-400" data-testid="eod-summary-error">{error}</p>}
          </div>

          {/* Optional fields */}
          <div className="space-y-1">
            <span className="text-[13px] font-medium">Blockers <span className="text-muted-foreground font-normal">(optional)</span></span>
            <Textarea
              value={blockers}
              onChange={(e) => setBlockers(e.target.value)}
              placeholder="e.g. ‘Waiting on Hwy1 menu prices; need final colors from ESM.’"
              rows={2}
              className="text-[13px]"
              data-testid="eod-blockers"
            />
          </div>
          <div className="space-y-1">
            <span className="text-[13px] font-medium">Plan for tomorrow <span className="text-muted-foreground font-normal">(optional)</span></span>
            <Textarea
              value={planTomorrow}
              onChange={(e) => setPlanTomorrow(e.target.value)}
              placeholder="e.g. ‘Schedule DICH stories, finalize Hwy1 posts, refine ESM logo.’"
              rows={2}
              className="text-[13px]"
              data-testid="eod-plan-tomorrow"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={submitting} onClick={() => onOpenChange(false)} data-testid="eod-cancel">
            Go back (stay clocked in)
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="eod-submit">
            {submitting ? 'Saving…' : 'Submit EOD & Clock Out'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
